import {
  TComplianceCheckConfig,
  TComplianceCheckDeviceClassification,
  TComplianceCheckDeviceType,
  TComplianceCheckDocument,
  TComplianceCheckIssue,
  TComplianceCheckRegulation,
} from './types';

export const COMPLIANCE_CHECK_ANALYSIS_DURATION_MS = 10_000;

export const COMPLIANCE_CHECK_PROGRESS_STEPS = [
  'Reading controlled documents...',
  'Mapping compliance claims to supporting evidence...',
  'Checking for contradictions across the TD set...',
  'Checking completeness of required evidence...',
  'Checking standards, identifiers, and labeling consistency...',
  'Scoring logical and traceability gaps...',
] as const;

export const COMPLIANCE_CHECK_REGULATION_OPTIONS: readonly TComplianceCheckRegulation[] = [
  'EU_MDR',
  'EU_IVDR',
  'FDA_QMS',
  'ISO_13485',
];

export const COMPLIANCE_CHECK_REGULATION_LABELS: Record<TComplianceCheckRegulation, string> = {
  EU_MDR: 'EU MDR',
  EU_IVDR: 'EU IVDR',
  FDA_QMS: 'FDA (21 CFR 820 / 21 CFR 11)',
  ISO_13485: 'ISO 13485',
};

export const COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_OPTIONS: readonly TComplianceCheckDeviceClassification[] = [
  'CLASS_I',
  'CLASS_IIA',
  'CLASS_IIB',
  'CLASS_III',
];

export const COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_LABELS: Record<TComplianceCheckDeviceClassification, string> = {
  CLASS_I: 'Class I',
  CLASS_IIA: 'Class IIa',
  CLASS_IIB: 'Class IIb',
  CLASS_III: 'Class III',
};

export const COMPLIANCE_CHECK_DEVICE_TYPE_OPTIONS: readonly TComplianceCheckDeviceType[] = [
  'SOFTWARE_SAMD',
  'HARDWARE',
  'COMBINATION',
];

export const COMPLIANCE_CHECK_DEVICE_TYPE_LABELS: Record<TComplianceCheckDeviceType, string> = {
  SOFTWARE_SAMD: 'Active device intended for monitoring',
  HARDWARE: 'Hardware',
  COMBINATION: 'Combination',
};

export const DEFAULT_COMPLIANCE_CHECK_CONFIG: TComplianceCheckConfig = {
  regulation: 'EU_MDR',
  classification: 'CLASS_IIB',
  deviceType: 'SOFTWARE_SAMD',
};

export const MOCK_COMPLIANCE_CHECK_DOCUMENTS: TComplianceCheckDocument[] = [
  {
    id: 'gspr-checklist-report',
    name: 'General Safety & Performance Requirements Report.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Essential requirements and standards mapping',
    status: 'ANALYZED',
  },
  {
    id: 'electrical-safety-emc-test-report',
    name: 'Electrical Safety & EMC Test Report.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Electrical safety and EMC evidence',
    status: 'ANALYZED',
  },
  {
    id: 'risk-management-report',
    name: 'Risk Management Report.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Hazards, controls, and residual-risk traceability',
    status: 'ANALYZED',
  },
  {
    id: 'device-description-intended-purpose',
    name: 'Device Description & Intended Purpose.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Claims, intended use, and product definition',
    status: 'ANALYZED',
  },
  {
    id: 'clinical-evaluation-report',
    name: 'Clinical Evaluation Report.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Clinical evidence and performance claims',
    status: 'ANALYZED',
  },
  {
    id: 'standards-matrix',
    name: 'Standards Matrix.xlsx',
    itemType: 'FILE',
    mimetype: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    reviewScope: 'State-of-the-art standards traceability',
    status: 'ANALYZED',
  },
  {
    id: 'labelling-pack',
    name: 'Labelling Pack.pdf',
    itemType: 'FILE',
    mimetype: 'application/pdf',
    reviewScope: 'UDI, labeling, and identification consistency',
    status: 'ANALYZED',
  },
  {
    id: 'declaration-of-conformity',
    name: 'Declaration of Conformity.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Regulatory declaration consistency',
    status: 'ANALYZED',
  },
  {
    id: 'udi-assignment-register',
    name: 'UDI Assignment Register.xlsx',
    itemType: 'FILE',
    mimetype: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    reviewScope: 'Identifier readiness and labeling handoff',
    status: 'ANALYZED',
  },
  {
    id: 'td-master-index',
    name: 'TD Master Index.docx',
    itemType: 'DOCUMENT',
    mimetype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    reviewScope: 'Controlled-document completeness',
    status: 'ANALYZED',
  },
];

export const MOCK_COMPLIANCE_CHECK_ISSUES: TComplianceCheckIssue[] = [
  {
    id: 'electrical-safety-standard-edition-mismatch',
    title: 'Electrical safety standard edition mismatch',
    severity: 'MAJOR',
    documentIds: ['gspr-checklist-report', 'electrical-safety-emc-test-report'],
    error:
      'The GSPR claims compliance to IEC/EN 60601-1 Ed 3.1, but the referenced test report only demonstrates testing to Ed 3.0. This creates a traceability and evidence gap for the claimed state of the art.',
    recommendedAction:
      'Update the GSPR / standards matrix to the actually tested edition, or provide bridging rationale / delta assessment / updated test evidence demonstrating Ed 3.1 coverage.',
    resolved: false,
  },
  {
    id: 'out-of-scope-accessory-hazard',
    title: 'Risk file contains a clearly out-of-scope accessory hazard',
    severity: 'MAJOR',
    documentIds: ['risk-management-report', 'device-description-intended-purpose'],
    error:
      'Risk file includes “Infection transmission due to inadequate sterilization of reusable positioning pad (accessory)” with EO sterilization validation evidence, while the device description says the product is software-only and supplied non-sterile.',
    recommendedAction:
      'Remove the sterilization/accessory hazard unless such an accessory actually belongs to the marketed device. Then re-check related GSPR applicability, IFU content, and referenced evidence for consistency.',
    resolved: false,
  },
  {
    id: 'udi-assignment-pending',
    title: 'UDI assignment still pending although TD claims product readiness',
    severity: 'MAJOR',
    documentIds: ['risk-management-report'],
    error:
      'A core device identification element is still marked as pending. For a technical documentation package presented as conformity-ready, that is a significant completeness gap.',
    recommendedAction:
      'Assign Basic UDI-DI / UDI-DI, update labeling and device identification sections, and verify consistency across the TD master index, labeling pack, and declaration-related sections.',
    resolved: false,
  },
  {
    id: 'clinical-evaluation-integration-pending',
    title: 'Clinical evaluation integration is still pending',
    severity: 'MAJOR',
    documentIds: ['risk-management-report', 'gspr-checklist-report'],
    error:
      'The risk report states: “Clinical evaluation report per MDR Annex XIV … integration into TD set pending,” while the GSPR report marks clinical evaluation-related compliance as “Compliant.” That creates a potential completeness contradiction.',
    recommendedAction:
      'Add the clinical evaluation report into the controlled TD set, or downgrade the status from fully compliant to pending/open until the evidence package is complete.',
    resolved: false,
  },
  {
    id: 'lvo-claim-under-specified',
    title: 'LVO claim may be under-specified for NCCT-only intended purpose',
    severity: 'MAJOR',
    documentIds: ['device-description-intended-purpose', 'risk-management-report'],
    error:
      'The intended purpose claims the software flags studies with patterns consistent with “(a) suspected ICH and (b) suspected large vessel occlusion (LVO) indicators),” while the product is limited to non-contrast head CT (NCCT) only.',
    recommendedAction:
      'Tighten the intended purpose wording for LVO, define the exact imaging surrogate / output claim, and ensure the clinical evaluation and performance documentation explicitly support that precise claim.',
    resolved: false,
  },
  {
    id: 'missing-evidence-package-references',
    title: 'Evidence package references key documents that are not present in the reviewed TD subset',
    severity: 'MAJOR',
    documentIds: ['gspr-checklist-report', 'risk-management-report'],
    error:
      'Multiple compliance claims depend on key evidence files that are not part of the reviewed package subset. For a document review workflow, this should be flagged as an evidence not available / not reviewable issue.',
    recommendedAction:
      'Request the missing controlled documents before closing the review, or mark all dependent GSPR and risk-control claims as provisional until the evidence is available.',
    resolved: false,
  },
];

const documentById = Object.fromEntries(
  MOCK_COMPLIANCE_CHECK_DOCUMENTS.map((document) => [document.id, document])
) as Record<string, TComplianceCheckDocument>;

const documentFindingCounts = MOCK_COMPLIANCE_CHECK_ISSUES.reduce<Record<string, number>>((acc, issue) => {
  issue.documentIds.forEach((documentId) => {
    acc[documentId] = (acc[documentId] ?? 0) + 1;
  });
  return acc;
}, {});

export const getComplianceCheckDocument = (documentId: string) => documentById[documentId];

export const getComplianceCheckDocumentFindingCount = (documentId: string) => documentFindingCounts[documentId] ?? 0;

export const getTopComplianceCheckDocuments = (limit = 4) =>
  Object.entries(documentFindingCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, limit)
    .flatMap(([documentId, findings]) => {
      const document = getComplianceCheckDocument(documentId);
      return document ? [{ document, findings }] : [];
    });

export const getComplianceCheckReportSummary = () => {
  const totalIssues = MOCK_COMPLIANCE_CHECK_ISSUES.length;
  const resolvedIssues = MOCK_COMPLIANCE_CHECK_ISSUES.filter((issue) => issue.resolved).length;
  const unresolvedIssues = totalIssues - resolvedIssues;

  return {
    totalDocuments: MOCK_COMPLIANCE_CHECK_DOCUMENTS.length,
    totalIssues,
    resolvedIssues,
    unresolvedIssues,
  };
};

const parseOption = <TValue extends string>(
  value: string | null,
  allowedValues: readonly TValue[],
  fallback: TValue
): TValue => {
  if (!value) return fallback;
  return allowedValues.includes(value as TValue) ? (value as TValue) : fallback;
};

export const getComplianceCheckConfigFromSearchParams = (searchParams: URLSearchParams): TComplianceCheckConfig => ({
  regulation: parseOption(
    searchParams.get('regulation'),
    COMPLIANCE_CHECK_REGULATION_OPTIONS,
    DEFAULT_COMPLIANCE_CHECK_CONFIG.regulation
  ),
  classification: parseOption(
    searchParams.get('classification'),
    COMPLIANCE_CHECK_DEVICE_CLASSIFICATION_OPTIONS,
    DEFAULT_COMPLIANCE_CHECK_CONFIG.classification
  ),
  deviceType: parseOption(
    searchParams.get('deviceType'),
    COMPLIANCE_CHECK_DEVICE_TYPE_OPTIONS,
    DEFAULT_COMPLIANCE_CHECK_CONFIG.deviceType
  ),
});

export const createComplianceCheckResultUrl = ({
  productId,
  submissionId,
  config,
}: {
  productId: string;
  submissionId: string;
  config: TComplianceCheckConfig;
}) => {
  const searchParams = new URLSearchParams({
    regulation: config.regulation,
    classification: config.classification,
    deviceType: config.deviceType,
  });

  return `/product/${productId}/submissions/${submissionId}/compliance-check?${searchParams.toString()}`;
};
